import useScrollIntoView from '@/hooks/useScrollIntoView';
import { DropdownActionBarProps } from '@/storybook/components/FormCollection/components/DropdownActionBar/DropdownActionBar';
import DropdownButton, {
  DropdownButtonSize,
} from '@/storybook/components/FormCollection/components/DropdownButton/DropdownButton';
import DropdownList from '@/storybook/components/FormCollection/components/DropdownList/DropdownList';
import { useFloatingDropdownPanel } from '@/storybook/components/FormCollection/components/DropdownList/useFloatingDropdownPanel';
import VirtualizedRows from '@/storybook/components/FormCollection/components/VirtualizedRows/VirtualizedRows';
import { useCollapsibleCategories } from '@/storybook/components/FormCollection/hooks/useCollapsibleCategories';
import {
  DropdownPosition,
  createStableObjectHash,
  extractSelectItemLabelText,
  handleDropdownListPosition,
} from '@/storybook/components/FormCollection/utils/utils';
import { SelectItem } from '@/types/ui';
import cx from 'classnames';
import { JSX, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SingleSelectFloatingLabel.module.css';
import { useSingleSelectVirtualizationFloatingLabel } from './SingleSelectFloatingLabel/hooks/useSingleSelectVirtualizationFloatingLabel';

export type SingleSelectFloatingLabelProps<T = string> = {
  'label'?: ReactNode;
  'tooltip'?: ReactNode;
  'options': SelectItem<T>[];
  'isLoading'?: boolean;
  'value'?: T;
  'onChange': (selectedValue?: T) => void;
  'onClear'?: VoidFunction;
  'placeholder'?: string | JSX.Element;
  'disabled'?: boolean;
  'clearable'?: boolean;
  'adobeEventTrackFunctions'?: VoidFunction;
  'onDropdownToggle'?: (isOpen: boolean) => void;
  'alignment'?: 'vertical' | 'horizontal';
  'data-testid'?: string;
  'dropdownButtonTestId'?: string;
  'dropdownListTestId'?: string;
  'dropdownPosition'?: DropdownPosition;
  'customSelectItemFn'?: (value: T, options: SelectItem<T>[]) => SelectItem<T> | undefined;
  'size'?: keyof typeof DropdownButtonSize;
  'className'?: string;
  'state'?: 'default' | 'filled' | 'error';
  'actionBarProps'?: DropdownActionBarProps;
  'collapsibleCategories'?: boolean;
  'defaultCategoriesCollapsed'?: boolean;
};

const getValueKey = (val: unknown) =>
  typeof val === 'object' && val !== null ? createStableObjectHash(val as object) : String(val);

export default function SingleSelectFloatingLabel<T>({
  label,
  tooltip,
  options,
  isLoading = false,
  value,
  onChange,
  onClear,
  placeholder,
  disabled = false,
  clearable = true,
  adobeEventTrackFunctions,
  onDropdownToggle,
  alignment = 'vertical',
  'data-testid': dataTestId = 'singleSelectFloatingLabelDropdown',
  dropdownButtonTestId,
  dropdownListTestId,
  dropdownPosition,
  customSelectItemFn,
  size = 'L',
  className,
  state = 'default',
  actionBarProps,
  collapsibleCategories = false,
  defaultCategoriesCollapsed = false,
}: SingleSelectFloatingLabelProps<T>) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [dropdownListPosition, setDropdownLitPosition] = useState<DropdownPosition>(dropdownPosition ?? 'down');
  const { t } = useTranslation();

  const { isCollapsibleActive, isCategoryCollapsed, toggleCategory, resetCategories } = useCollapsibleCategories({
    isEnabled: collapsibleCategories,
    isDefaultCollapsed: defaultCategoriesCollapsed,
    isSearching: false,
  });

  const { dropdownRef, panelRef } = useFloatingDropdownPanel({ isOpen, onClose: () => setIsOpen(false) });
  const dropdownButtonRef = useRef<HTMLDivElement>(null);

  useScrollIntoView({
    elementRef: dropdownRef,
    isVisible: isOpen,
  });

  const fixedValue = useMemo(() => {
    return value != null ? [value] : [];
  }, [value]);

  const fixedOptions = useMemo(() => {
    if (options == null) return [];
    return options.filter((opt): opt is SelectItem<T> => opt != null && opt.value != null && opt.label != null);
  }, [options]);

  const fixedOptionsLength = fixedOptions.length;

  const handleToggleDropdown = useCallback(() => {
    if (disabled) return;
    setIsOpen(prev => {
      const newState = !prev;
      onDropdownToggle?.(newState);
      if (newState) {
        handleDropdownListPosition(dropdownPosition, dropdownRef, setDropdownLitPosition);
        resetCategories();
      }
      return newState;
    });
  }, [disabled, dropdownPosition, dropdownRef, onDropdownToggle, resetCategories]);

  const handleOnClearValue = useCallback(() => {
    if (disabled || !clearable) return;
    if (onClear) {
      onClear();
    } else {
      onChange(undefined);
    }
  }, [onClear, disabled, clearable, onChange]);

  const handleClickOption = useCallback(
    (selectedValue: T) => {
      if (disabled) return;
      onChange(selectedValue);
      setIsOpen(false);
    },
    [onChange, disabled],
  );

  const { virtualizedList, parentRef, renderVirtualRow } = useSingleSelectVirtualizationFloatingLabel({
    options: fixedOptions,
    selectedValues: fixedValue,
    disabled,
    noCategoryLabel: t('components.dropdownPanel.noCategory'),
    onOptionClick: handleClickOption,
    collapsibleCategories: isCollapsibleActive,
    isCategoryCollapsed,
    onToggleCategory: toggleCategory,
  });

  const dropdownButtonValue = useMemo(() => {
    if (value == null) return [];

    if (customSelectItemFn) {
      const customItem = customSelectItemFn(value, fixedOptions);
      return customItem ? [customItem] : [];
    }

    const valueKey = getValueKey(value);
    const foundOption = fixedOptions.find(opt => getValueKey(opt.value) === valueKey);

    return foundOption ? [foundOption] : [];
  }, [value, fixedOptions, customSelectItemFn]);

  const hasValue = dropdownButtonValue.length > 0;
  const isLabelFloating = hasValue || isOpen;
  const displayLabel = label || placeholder;
  const selectedOption = dropdownButtonValue[0];
  const selectedOptionLabel = selectedOption?.category
    ? t('components.dropdownPanel.optionWithCategory', {
        category: selectedOption.category,
        label: extractSelectItemLabelText(selectedOption),
      })
    : selectedOption?.label;

  return (
    <div
      ref={dropdownRef}
      className={cx(styles.floatingLabelContainer, className)}
      data-alignment={alignment}
      data-testid={dataTestId}
      onClick={adobeEventTrackFunctions}
      data-state={state}
    >
      <section className={styles.dropdownContainer}>
        {displayLabel && (
          <label
            className={styles.floatingLabel}
            data-floating={isLabelFloating}
            data-has-value={hasValue}
            data-testid="floatingLabel"
          >
            {displayLabel}
          </label>
        )}

        <div className={styles.buttonWrapper}>
          <DropdownButton
            ref={dropdownButtonRef}
            type="singleSelect"
            size={size}
            placeholder=""
            numOfValue={dropdownButtonValue.length}
            active={isOpen}
            onClick={!disabled ? handleToggleDropdown : undefined}
            onClear={handleOnClearValue}
            clearable={clearable}
            disabled={disabled}
            data-testid={dropdownButtonTestId}
          >
            <div className={styles.value} data-testid="value-singleSelectFloatingLabel-list">
              <div className={styles.valueText}>
                <div className={styles.label}>{selectedOptionLabel}</div>
                <span className={styles.additionalInfo}>{selectedOption?.additionalInfo}</span>
              </div>
            </div>
          </DropdownButton>
        </div>

        <DropdownList
          isOpen={isOpen}
          isLoading={isLoading}
          disabled={disabled}
          position={dropdownListPosition}
          data-testid={dropdownListTestId}
          numberOfSearchResults={fixedOptionsLength}
          listRef={parentRef}
          dropdownTriggerRef={dropdownButtonRef}
          isFloating
          panelRef={panelRef}
          optionsCount={fixedOptionsLength}
          actionBarProps={actionBarProps}
        >
          <VirtualizedRows virtualization={virtualizedList} renderRow={renderVirtualRow} />
        </DropdownList>
      </section>
      {tooltip && <div className={styles.tooltip}>{tooltip}</div>}
    </div>
  );
}
